import { httpCreators, HttpResponse } from '@ppvrbh/merchant-frontend-core';
import { put, takeLatest } from 'redux-saga/effects';
import { message } from 'antd';
import { MODIFIER_GROUP_REQUEST_ID_LIST } from './modifier-group-constant';
import { modifierGroupCreators } from './modifier-group-action';
import { MODIFIER_GROUP_KEY } from './modifier-group-reducer';
import { CallAllModifierGroupsAction, CallDeleteModifierGroupAction, CallModifierGroupsAction, CallCreateModifierGroupAction, CallUpdateModifierGroupAction, MODIFIER_GROUP_ACTION_TYPE } from './modifier-group-type';
import { ModifierGroupService } from './modifier-group-service';

function* callModifierGroups(action: CallModifierGroupsAction) {
  yield put(httpCreators.request({
    id: MODIFIER_GROUP_REQUEST_ID_LIST.GET_MODIFIER_GROUPS,
    sagaHandler: getModifierGroupsHandler,
    service: ModifierGroupService.getMenuModifierGroup,
    serviceParams: action.payload,
    callbackParams: { keyword: action.payload?.keyword },
  }));
}

function* getModifierGroupsHandler(resp: HttpResponse, callbackParams?: { keyword?: string }) {
  if (resp.success) {
    yield put(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.MODIFIER_GROUPS, resp.data.menuModifierGroups));
    yield put(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.META, resp.data.meta));
    if (!callbackParams?.keyword) {
      yield put(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.TOTAL_GROUPS, resp.data.meta?.totalItems));
    }
  } else {
    yield put(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.MODIFIER_GROUPS, []));
    yield put(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.META, undefined));
  }
}

function* callAllModifierGroups(action: CallAllModifierGroupsAction) {
  yield put(httpCreators.request({
    id: MODIFIER_GROUP_REQUEST_ID_LIST.GET_ALL_MODIFIER_GROUPS,
    sagaHandler: getAllModifierGroupsHandler,
    callbackParams: action.payload.onSuccessCallback,
    service: ModifierGroupService.getMenuModifierGroup,
  }));
}

function* getAllModifierGroupsHandler(resp: HttpResponse, callbackParams: any) {
  if (resp.success) {
    const groups = resp.data.menuModifierGroups;
    yield put(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.ALL_MODIFIER_GROUPS, groups));
    callbackParams?.(groups);
  }
}

function* updateModifierGroupSequenceHandler(resp: HttpResponse, callbackParams: any) {
  if (resp.success) {
    message.success('บันทึกลำดับกลุ่มตัวเลือกสำเร็จ');
    callbackParams?.();
  } else {
    message.error('บันทึกลำดับไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
  }
}

function* callCreateModifierGroup(action: CallCreateModifierGroupAction) {
  yield put(httpCreators.request({
    id: MODIFIER_GROUP_REQUEST_ID_LIST.CREATE_MODIFIER_GROUP,
    sagaHandler: createModifierGroupHandler,
    callbackParams: { onSuccess: action.payload.onSuccess, onError: action.payload.onError },
    service: ModifierGroupService.createMenuModifierGroup,
    serviceParams: action.payload.data,
  }));
}

function* createModifierGroupHandler(resp: HttpResponse, callbackParams: any) {
  if (resp.success) {
    message.success('สร้างกลุ่มตัวเลือกสำเร็จ');
    callbackParams?.onSuccess?.();
  } else {
     const translationKeys: string[] | string = resp.data?.translationKey ?? [];
    if (translationKeys.includes('menuModifierGroup.allMenuModifierItemsOffline')) {
      message.error('ไม่มีตัวเลือกที่เปิดใช้งานในกลุ่มนี้ กรุณาเปลี่ยนเงื่อนไขในการเลือก หรือเปลี่ยนสถานะตัวเลือก');
      return;
    }
    
    if (translationKeys.length > 0 && callbackParams?.onError) {
      callbackParams.onError(translationKeys);
    } else {
      message.error('สร้างกลุ่มตัวเลือกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
    }
  }
}

function* callUpdateModifierGroup(action: CallUpdateModifierGroupAction) {
  yield put(httpCreators.request({
    id: MODIFIER_GROUP_REQUEST_ID_LIST.UPDATE_MODIFIER_GROUP,
    sagaHandler: updateModifierGroupHandler,
    callbackParams: { onSuccess: action.payload.onSuccess, onError: action.payload.onError },
    service: ModifierGroupService.updateMenuModifierGroup,
    serviceParams: action.payload.data,
  }));
}

function* updateModifierGroupHandler(resp: HttpResponse, callbackParams: any) {
  if (resp.success) {
    message.success('แก้ไขกลุ่มตัวเลือกสำเร็จ');
    callbackParams?.onSuccess?.();
  } else {
    const translationKeys: string[] | string = resp.data?.translationKey ?? [];
    if (translationKeys.includes('menuModifierGroup.allMenuModifierItemsOffline')) {
      message.error('ไม่มีตัวเลือกที่เปิดใช้งานในกลุ่มนี้ กรุณาเปลี่ยนเงื่อนไขในการเลือก หรือเปลี่ยนสถานะตัวเลือก');
      return;
    }
    if (translationKeys.length > 0 && callbackParams?.onError) {
      callbackParams.onError(translationKeys);
    } else {
      message.error('แก้ไขกลุ่มตัวเลือกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
    }
  }
}

function* callDeleteModifierGroup(action: CallDeleteModifierGroupAction) {
  yield put(httpCreators.request({
    id: MODIFIER_GROUP_REQUEST_ID_LIST.DELETE_MODIFIER_GROUP,
    sagaHandler: deleteModifierGroupHandler,
    service: ModifierGroupService.deleteMenuModifierGroup,
    serviceParams: { id: action.payload.id },
  }));
}

function* deleteModifierGroupHandler(resp: HttpResponse) {
  if (resp.success) {
    message.success('ลบกลุ่มตัวเลือกสำเร็จ');
    yield put(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.IS_DELETE_SUCCESS, true));
  } else {
    message.error('ลบกลุ่มตัวเลือกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
  }
}

export const modifierGroupSagas = {
  callModifierGroups,
  getModifierGroupsHandler,
  callAllModifierGroups,
  getAllModifierGroupsHandler,
  updateModifierGroupSequenceHandler,
  callDeleteModifierGroup,
  deleteModifierGroupHandler,
  callCreateModifierGroup,
  createModifierGroupHandler,
  callUpdateModifierGroup,
  updateModifierGroupHandler,
  watchers: [
    takeLatest(MODIFIER_GROUP_ACTION_TYPE.CALL_MODIFIER_GROUPS, callModifierGroups),
    takeLatest(MODIFIER_GROUP_ACTION_TYPE.CALL_ALL_MODIFIER_GROUPS, callAllModifierGroups),
    takeLatest(MODIFIER_GROUP_ACTION_TYPE.CALL_DELETE_MODIFIER_GROUP, callDeleteModifierGroup),
    takeLatest(MODIFIER_GROUP_ACTION_TYPE.CALL_CREATE_MODIFIER_GROUP, callCreateModifierGroup),
    takeLatest(MODIFIER_GROUP_ACTION_TYPE.CALL_UPDATE_MODIFIER_GROUP, callUpdateModifierGroup),
  ],
};
